import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ExhibitionArtistData, ExhibitionData } from '../../../shared/dto/EntityRepository';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import {
  BlackFitBtnHeartbeat,
  PaperBlurFitBtn,
} from '../../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { WorkViewerInfo, WorkViewerTitle } from '../../../shared/component/headless/input/InputBody';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { isValidHttpUrl } from '../../../shared/util/urlValidation';
import editIcon from '../../../asset/icon/edit.png';
import seeIcon from '../../../asset/icon/see.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import { useAdminExhibition } from '../../share/hook/useAdminExhibition';
import { useAdminExhibitionStore } from '../../share/store/adminExhibitionStore';
import AdminMemberInfo from '../members/AdminMemberInfo';
import {
  getExhibitionDraftValidationError,
  isSameExhibitionDraft,
  normalizeExhibitionDetail,
  toCreateExhibitionReq,
  toUpdateExhibitionReq,
} from './exhibitionDraft';
import { runAfterUnsavedExhibitionCheck } from './runAfterUnsavedExhibitionCheck';

const emptyArtist = (): ExhibitionArtistData => ({
  artistId: null,
  name: '',
  nameEn: '',
  instagramHandle: '',
  architraveHandle: '',
});

const isExplicitHttpUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value.trim()) && isValidHttpUrl(value);

const ExhibitionViewer: React.FC = () => {
  const {
    activeExhibition,
    setActiveExhibition,
    clearExhibitionSelection,
    create,
    update,
    remove,
    bulkResult,
  } = useAdminExhibition();
  const setHasChanged = useAdminExhibitionStore((state) => state.setHasChanged);
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const [isEditing, setIsEditing] = useState(false);
  const originalRef = useRef<ExhibitionData | null>(null);
  const createBaselineRef = useRef<ExhibitionData | null>(null);

  const detail = activeExhibition;
  const isCreating = !!detail && detail.id <= 0;
  const hasChanged = (() => {
    if (!detail) return false;
    if (isCreating && createBaselineRef.current) {
      return !isSameExhibitionDraft(createBaselineRef.current, detail);
    }
    if (isEditing && originalRef.current) {
      return !isSameExhibitionDraft(originalRef.current, detail);
    }
    return false;
  })();

  useEffect(() => {
    setIsEditing(false);
    originalRef.current = null;
    createBaselineRef.current = isCreating && detail
      ? normalizeExhibitionDetail(detail)
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  useEffect(() => {
    setHasChanged(hasChanged);
  }, [hasChanged, setHasChanged]);

  useEffect(() => () => {
    const state = useAdminExhibitionStore.getState();
    const current = state.activeExhibition;
    const original = originalRef.current;
    const createBaseline = createBaselineRef.current;

    if (original && current?.id === original.id) {
      state.setActiveExhibition(original);
    } else if (createBaseline && current?.id === createBaseline.id) {
      state.clearExhibitionSelection();
    }
    state.setHasChanged(false);
  }, []);

  if (!detail) return null;

  const updateDraft = (updates: Partial<ExhibitionData>) => {
    setActiveExhibition({ ...detail, ...updates });
  };

  const updateArtist = (index: number, updates: Partial<ExhibitionArtistData>) => {
    updateDraft({
      artists: detail.artists.map((artist, artistIndex) =>
        artistIndex === index ? { ...artist, ...updates } : artist
      ),
    });
  };

  const validate = (): boolean => {
    const validationError = getExhibitionDraftValidationError(detail);
    if (validationError === 'MISSING_REQUIRED'
      || detail.artists.some((artist) => !artist.name.trim())) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return false;
    }
    if (
      validationError === 'INVALID_DATE'
      || !isExplicitHttpUrl(detail.sourceUrl)
      || (!!detail.officialUrl && !isExplicitHttpUrl(detail.officialUrl))
    ) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_VALUE_REQUESTED'),
      });
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    await create(toCreateExhibitionReq(detail));
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    const saved = await update(detail.id, toUpdateExhibitionReq(detail));
    if (!saved) return;
    originalRef.current = null;
    setIsEditing(false);
  };

  const enterEdit = () => {
    const normalized = normalizeExhibitionDetail(detail);
    originalRef.current = normalized;
    setActiveExhibition(normalized);
    setIsEditing(true);
  };

  const exitEdit = () => {
    if (originalRef.current) setActiveExhibition(originalRef.current);
    originalRef.current = null;
    setIsEditing(false);
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_EXHIBITION', { title: detail.title }),
      callBack: () => { void remove(detail.id); },
    });
  };

  if (isCreating || isEditing) {
    return (
      <ViewerContainer data-has-changed={hasChanged ? 'true' : 'false'}>
        <ScrollBody>
          <FormContainer>
            <HeadlessInput
              value={detail.title}
              handleChange={(event) => updateDraft({ title: event.target.value })}
              placeholder="Exhibition title"
              StyledInput={TitleInput}
            />
            <HeadlessInput
              value={detail.titleEn}
              handleChange={(event) => updateDraft({ titleEn: event.target.value })}
              placeholder="Exhibition title (EN)"
              StyledInput={SubtitleInput}
            />
            <InfoForm>
              <FormRow label="Venue code">
                <FormInput
                  value={detail.venueCode}
                  onChange={(value) => updateDraft({ venueCode: value })}
                  placeholder="MMCA-SEOUL"
                />
              </FormRow>
              <FormRow label="Start date">
                <FormInput
                  type="date"
                  value={detail.startDate}
                  onChange={(value) => updateDraft({ startDate: value })}
                  placeholder="YYYY-MM-DD"
                />
              </FormRow>
              <FormRow label="End date">
                <FormInput
                  type="date"
                  value={detail.endDate}
                  onChange={(value) => updateDraft({ endDate: value })}
                  placeholder="YYYY-MM-DD"
                />
              </FormRow>
              <FormRow label="Official URL">
                <FormInput
                  value={detail.officialUrl ?? ''}
                  onChange={(value) => updateDraft({ officialUrl: value })}
                  placeholder="https://... (optional)"
                />
              </FormRow>
              <FormRow label="Source URL">
                <FormInput
                  value={detail.sourceUrl}
                  onChange={(value) => updateDraft({ sourceUrl: value })}
                  placeholder="https://..."
                />
              </FormRow>
            </InfoForm>
            <ArtistHeader>
              <span>Artists</span>
              <SmallButton type="button" onClick={() => updateDraft({
                artists: [...detail.artists, emptyArtist()],
              })}>
                + Artist
              </SmallButton>
            </ArtistHeader>
            <ArtistList>
              {detail.artists.map((artist, index) => (
                <ArtistCard key={`credit-${index}`}>
                  <ArtistCardHeader>
                    <span>Artist {index + 1}</span>
                    <RemoveButton
                      type="button"
                      aria-label={`Remove artist ${index + 1}`}
                      onClick={() => updateDraft({
                        artists: detail.artists.filter((_, artistIndex) => artistIndex !== index),
                      })}
                    >
                      ×
                    </RemoveButton>
                  </ArtistCardHeader>
                  <FormInput
                    value={artist.name}
                    onChange={(value) => updateArtist(index, { name: value })}
                    placeholder="Name"
                  />
                  <FormInput
                    value={artist.nameEn}
                    onChange={(value) => updateArtist(index, { nameEn: value })}
                    placeholder="Name (EN, optional)"
                  />
                  <FormInput
                    value={artist.instagramHandle}
                    onChange={(value) => updateArtist(index, { instagramHandle: value })}
                    placeholder="Instagram handle"
                  />
                  <FormInput
                    value={artist.architraveHandle}
                    onChange={(value) => updateArtist(index, { architraveHandle: value })}
                    placeholder="Architrave handle"
                  />
                </ArtistCard>
              ))}
            </ArtistList>
            {isCreating && (
              <CreateButtonContainer>
                <HeadlessBtn
                  value="Create"
                  handleClick={() => { void handleCreate(); }}
                  StyledBtn={BlackFitBtnHeartbeat}
                />
                <HeadlessBtn
                  value="Cancel"
                  handleClick={() => runAfterUnsavedExhibitionCheck(clearExhibitionSelection)}
                  StyledBtn={PaperBlurFitBtn}
                />
              </CreateButtonContainer>
            )}
          </FormContainer>
        </ScrollBody>
        {isEditing && (
          <ActionBar>
            {hasChanged && (
              <HeadlessBtn
                value="Update"
                handleClick={() => { void handleUpdate(); }}
                StyledBtn={BlackFitBtnHeartbeat}
              />
            )}
            <UniversalIconBtn
              aria-label="Cancel exhibition editing"
              size="fit"
              border={true}
              backgroundImg={seeIcon}
              onClick={exitEdit}
            />
          </ActionBar>
        )}
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer>
      <ScrollBody>
        {bulkResult && (
          <BackButton type="button" onClick={clearExhibitionSelection}>Results</BackButton>
        )}
        <Title>{detail.title}</Title>
        {detail.titleEn && <Meta>{detail.titleEn}</Meta>}
        <InfoContainer>
          <AdminMemberInfo label="Venue code" value={detail.venueCode} />
          <AdminMemberInfo label="Start date" value={detail.startDate} />
          <AdminMemberInfo label="End date" value={detail.endDate} />
          <AdminMemberInfo
            label="Official URL"
            value={detail.officialUrl || '-'}
            href={detail.officialUrl || undefined}
          />
          <AdminMemberInfo
            label="Source URL"
            value={detail.sourceUrl || '-'}
            href={detail.sourceUrl || undefined}
          />
        </InfoContainer>
        <ArtistSectionTitle>Artists</ArtistSectionTitle>
        <ArtistReadList>
          {detail.artists.length === 0 ? '-' : detail.artists.map((artist, index) => (
            <ArtistReadRow key={`credit-${index}-${artist.name}`}>
              <span>{artist.name}{artist.nameEn ? ` / ${artist.nameEn}` : ''}</span>
              <small>
                {artist.artistId != null
                  ? ([
                    artist.instagramHandle ? `IG @${artist.instagramHandle}` : '',
                    artist.architraveHandle ? `Architrave @${artist.architraveHandle}` : '',
                  ].filter(Boolean).join(' · ') || 'Linked artist')
                  : 'Credit only'}
              </small>
            </ArtistReadRow>
          ))}
        </ArtistReadList>
      </ScrollBody>
      <ActionBar>
        <UniversalIconBtn
          aria-label="Edit exhibition"
          size="fit"
          border={true}
          backgroundImg={editIcon}
          onClick={enterEdit}
        />
        <UniversalIconBtn
          aria-label="Delete exhibition"
          size="fit"
          border={true}
          backgroundImg={deleteCloseIcon}
          backgroundImgHover={deleteOpenIcon}
          onClick={handleDelete}
        />
      </ActionBar>
    </ViewerContainer>
  );
};

interface FormRowProps {
  label: string;
  children: React.ReactNode;
}

const FormRow: React.FC<FormRowProps> = ({ label, children }) => (
  <FieldRow>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{children}</FieldValue>
  </FieldRow>
);

interface FormInputProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  type?: string;
}

const FormInput: React.FC<FormInputProps> = ({
  value,
  placeholder,
  onChange,
  type,
}) => (
  <HeadlessInput
    type={type}
    value={value}
    handleChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    StyledInput={ExhibitionFormInput}
  />
);

const ViewerContainer = styled.section`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: 56px;
  &::-webkit-scrollbar { display: none; }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 16px;
`;

const InfoForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const TitleInput = styled(WorkViewerTitle)`
  margin-bottom: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const SubtitleInput = styled(WorkViewerInfo)`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const FieldRow = styled.div`
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldLabel = styled.span`
  flex-shrink: 0;
  min-width: 140px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const FieldValue = styled.div`
  flex: 1;
  min-width: 0;
`;

const ExhibitionFormInput = styled(WorkViewerInfo)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ArtistHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const SmallButton = styled.button`
  padding: 2px 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const ArtistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ArtistCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ArtistCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  cursor: pointer;
`;

const CreateButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5vh;
  padding-top: 8px;
`;

const ActionBar = styled.div`
  position: absolute;
  right: 0;
  bottom: 0.6vh;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5vh;
`;

const BackButton = styled.button`
  align-self: flex-start;
  padding: 0 0 8px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const Title = styled.h2`
  min-height: 28px;
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Meta = styled.div`
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ArtistSectionTitle = styled.div`
  padding: 18px 0 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ArtistReadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const ArtistReadRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 7px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};

  small {
    color: ${({ theme }) => theme.colors.color_Gray_05};
  }
`;

export default ExhibitionViewer;
